/**
 * Mobile sleep / lock survival.
 *
 * Stopgap-for-no-native-app strategy. Until the Flutter mobile app is the
 * shipping path, we lean as hard as the browser allows on staying awake:
 *
 *   1. Screen Wake Lock API: prevents idle auto-dim / auto-sleep while the
 *      page is foreground. Acquired as soon as the user opens the voice
 *      page (NOT just on connect) so the screen stays on while they're
 *      reading the linking instructions, picking a device, etc. The user
 *      pressing the power button is still respected. Chrome / Edge /
 *      Safari 16.4+ ship Wake Lock; Firefox doesn't, and we silently
 *      degrade.
 *
 *   2. Silent-audio keepalive: an <audio> element looping a 1-sample
 *      silent buffer at extremely low volume. Two effects:
 *        - Marks the page as "playing media" to the browser's tab-
 *          suspension logic, which on Android Chrome keeps the tab
 *          alive longer in the background.
 *        - Combined with the navigator.mediaSession registration the
 *          caller does separately, it nudges the OS into showing a
 *          persistent "this page is playing media" notification so
 *          backgrounded tabs aren't aggressively reaped.
 *      Volume is effectively 0 (silent buffer); the audio itself is not
 *      audible to the user.
 *
 *   3. visibilitychange listener: on hidden→visible transitions, fires
 *      onResume so the caller can resume the AudioContext, restart the
 *      mic stream if the OS ended it, and force a WS reconnect.
 *
 * All best-effort. A browser is not a native app; even with everything
 * above, iOS Safari will silence the mic the moment the screen locks.
 * Android Chrome is more forgiving and may keep PLAYBACK alive through a
 * lock if the user has battery-optimisation exemptions; mic capture is
 * still flaky there. The goal is "stays awake while in use, recovers
 * cleanly when not."
 */

type WakeLockSentinel = { release: () => Promise<void>; addEventListener: (type: string, listener: () => void) => void }
type NavigatorWithWakeLock = Navigator & {
  wakeLock?: { request: (kind: 'screen') => Promise<WakeLockSentinel> }
}

export interface SleepGuardOptions {
  /** Called when the page transitions from hidden to visible. */
  readonly onResume: () => void
  /** Logger hook. Defaults to console.warn for visibility into wake-lock
   *  drop reasons. Pass a no-op to silence. */
  readonly log?: (msg: string, err?: unknown) => void
  /**
   * When true, also start the silent-audio keepalive in addition to the
   * Wake Lock. Off by default because some users find ANY audio element
   * showing in the OS media controls intrusive; the voice page opts in.
   */
  readonly enableSilentKeepalive?: boolean
}

export class SleepGuard {
  private readonly opts: SleepGuardOptions
  private sentinel: WakeLockSentinel | null = null
  private active = false
  private readonly visibilityHandler: () => void
  private keepaliveAudio: HTMLAudioElement | null = null

  constructor(opts: SleepGuardOptions) {
    this.opts = opts
    this.visibilityHandler = (): void => {
      if (document.visibilityState !== 'visible') return
      // Re-acquire wake lock - sentinels are automatically released by
      // the browser whenever the page is backgrounded.
      void this.acquireLock()
      try { this.opts.onResume() } catch (err) {
        this.log('onResume threw', err)
      }
    }
  }

  /** Begin guarding. Acquires the wake lock and starts watching visibility. */
  async start(): Promise<void> {
    if (this.active) return
    this.active = true
    document.addEventListener('visibilitychange', this.visibilityHandler)
    await this.acquireLock()
    if (this.opts.enableSilentKeepalive === true) this.startKeepalive()
  }

  /** Release the wake lock and stop watching visibility. */
  async stop(): Promise<void> {
    if (!this.active) return
    this.active = false
    document.removeEventListener('visibilitychange', this.visibilityHandler)
    this.stopKeepalive()
    await this.releaseLock()
  }

  /** True after start() has successfully acquired a wake lock at least once. */
  get isHoldingWakeLock(): boolean {
    return this.sentinel !== null
  }

  private startKeepalive(): void {
    if (this.keepaliveAudio !== null) return
    try {
      // Tiny inline WAV: 1-sample silent mono. Looping this through an
      // <audio> element registers the page as "playing media" to the
      // browser without producing any audible output. Tested on Android
      // Chrome 122+ and iOS Safari 17.4+.
      const silentWavDataUri =
        'data:audio/wav;base64,UklGRkAAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YRwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'
      const audio = new Audio(silentWavDataUri)
      audio.loop = true
      audio.volume = 0.001 // effectively silent, but > 0 so the browser counts it as playing
      audio.preload = 'auto'
      // Don't await: autoplay can be denied if the user hasn't interacted
      // yet. The connect button click on the voice page is the user
      // gesture; play() resolves once that's happened. If it never does,
      // we still have the Wake Lock and the visibilitychange listener.
      void audio.play().catch((err) => this.log('silent-keepalive play deferred until user gesture', err))
      this.keepaliveAudio = audio
    } catch (err) {
      this.log('silent-keepalive setup failed', err)
    }
  }

  private stopKeepalive(): void {
    const a = this.keepaliveAudio
    this.keepaliveAudio = null
    if (a === null) return
    try { a.pause(); a.src = '' } catch { /* ignore */ }
  }

  /**
   * Belt-and-braces: callers can invoke this after a user gesture (the
   * Connect button click) to retry autoplay if it was deferred. Safe to
   * call repeatedly.
   */
  primeKeepaliveAfterUserGesture(): void {
    const a = this.keepaliveAudio
    if (a === null) return
    if (!a.paused) return
    void a.play().catch((err) => this.log('silent-keepalive retry failed', err))
  }

  private async acquireLock(): Promise<void> {
    if (!this.active) return
    if (this.sentinel !== null) return
    const nav = navigator as NavigatorWithWakeLock
    if (nav.wakeLock === undefined) return // Firefox + older browsers
    try {
      const sentinel = await nav.wakeLock.request('screen')
      this.sentinel = sentinel
      // The sentinel gets force-released when the page is hidden. We
      // re-request on the next visibilitychange.
      sentinel.addEventListener('release', () => {
        this.sentinel = null
      })
    } catch (err) {
      // Request can fail when the user has disabled wake locks in their
      // browser settings, or when the document is no longer visible.
      // Not fatal; the rest of the recovery path still works.
      this.log('wake-lock request failed', err)
    }
  }

  private async releaseLock(): Promise<void> {
    const s = this.sentinel
    this.sentinel = null
    if (s === null) return
    try { await s.release() } catch (err) { this.log('wake-lock release failed', err) }
  }

  private log(msg: string, err?: unknown): void {
    if (this.opts.log !== undefined) this.opts.log(msg, err)
    else console.warn(`[sleep] ${msg}`, err)
  }
}

/** Feature detection so the UI can show a helpful hint if unavailable. */
export function wakeLockSupported(): boolean {
  const nav = navigator as NavigatorWithWakeLock
  return nav.wakeLock !== undefined
}
